import React, { useCallback, useContext, useMemo, useState } from "react"
import { Children } from "../../../../../../../../../components/miscellianous/children"
import { BSParking } from "../../../../../../../../../core/dto/beem-shot/BSParking/BSParking"
import { BSParkingCreationDto } from "../../../../../../../../../core/dto/beem-shot/BSParking/BSParkingCreationDto"
import { BSParkingUpdateDto } from "../../../../../../../../../core/dto/beem-shot/BSParking/BSParkingUpdateDto"
import { useBSParking } from "../../../../../../../../../core/hooks/beem-shot/useBSParking"
import { BSParkingListContext } from "./BSParkingListContext"

export const BSParkingContext = React.createContext<BSParkingStore>({} as BSParkingStore)

export function BSParkingContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { postBSParking, putBSParking, deleteBSParking } = useBSParking()

  const { setBSParkingList } = useContext(BSParkingListContext)

  const [isParkingFormOpen, setIsParkingFormOpen] = useState<boolean>(false)
  const [isParkingFormForSuperstructure, setIsParkingFormForSuperstructure] = useState<boolean>(false)
  const [selectedBSParking, setSelectedBSParking] = useState<BSParking>()

  const createBSParking: (bsParkingCreationDto: BSParkingCreationDto) => Promise<BSParking> = useCallback(
    (bsParkingCreationDto) =>
      postBSParking(bsParkingCreationDto).then((newBSParking) => {
        setSelectedBSParking(newBSParking)
        setBSParkingList((prev: BSParking[]) => [...prev, newBSParking])
        return newBSParking
      }),
    [postBSParking, setBSParkingList]
  )

  const updateBSParking: (bsParkingUpdateDto: BSParkingUpdateDto) => Promise<BSParking> = useCallback(
    (bsParkingUpdateDto) =>
      putBSParking(bsParkingUpdateDto).then((updatedBSParking) => {
        setSelectedBSParking(updatedBSParking)
        setBSParkingList((prev) => prev.map((parking) => (parking.id === updatedBSParking.id ? updatedBSParking : parking)))
        return updatedBSParking
      }),
    [putBSParking, setBSParkingList]
  )

  const deleteBSParkingById: (bsParkingId: string) => Promise<void> = useCallback(
    (bsParkingId) =>
      deleteBSParking(bsParkingId).then(() => {
        setSelectedBSParking(undefined)
        setBSParkingList((prev) => prev.filter((parking) => parking.id !== bsParkingId))
      }),
    [deleteBSParking, setBSParkingList]
  )

  const bsParkingStore: BSParkingStore = useMemo(
    () => ({
      selectedBSParking,
      isParkingFormOpen,
      isParkingFormForSuperstructure,
      setIsParkingFormForSuperstructure,
      setIsParkingFormOpen,
      setSelectedBSParking,
      createBSParking,
      updateBSParking,
      deleteBSParkingById,
    }),
    [
      selectedBSParking,
      isParkingFormOpen,
      isParkingFormForSuperstructure,
      setIsParkingFormForSuperstructure,
      setIsParkingFormOpen,
      setSelectedBSParking,
      createBSParking,
      updateBSParking,
      deleteBSParkingById,
    ]
  )
  return <BSParkingContext.Provider value={bsParkingStore}>{children}</BSParkingContext.Provider>
}

export type BSParkingStore = {
  selectedBSParking: BSParking | undefined
  isParkingFormOpen: boolean
  isParkingFormForSuperstructure: boolean
  setIsParkingFormForSuperstructure: React.Dispatch<React.SetStateAction<boolean>>
  setIsParkingFormOpen: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedBSParking: React.Dispatch<React.SetStateAction<BSParking | undefined>>
  createBSParking: (bsParkingCreationDto: BSParkingCreationDto) => Promise<BSParking>
  updateBSParking: (bsParkingUpdateDto: BSParkingUpdateDto) => Promise<BSParking>
  deleteBSParkingById: (bsParkingId: string) => Promise<void>
}
